/**
 * Cognitive Store - State management for cognitive AI features
 *
 * Manages:
 * - Reasoning chains and decision explanations
 * - Similar cases from episodic memory
 * - Confidence intervals and uncertainty quantification
 * - Real-time cognitive updates via WebSocket
 * - Evaluation metrics and feedback
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type {
  CognitiveOutput,
  ReasoningStep,
  SimilarCase,
  RootCauseAnalysis
} from '@/types/prediction'

// Cognitive processing mode
export type CognitiveMode = 'reactive' | 'deliberative' | 'learning' | 'exploratory'

// Confidence interval data
export interface ConfidenceInterval {
  lower: number
  upper: number
  level: number // e.g., 0.95 for 95% CI
}

// Uncertainty source
export interface UncertaintySource {
  source: string
  contribution: number // 0-1 scale
  description: string
  reducible: boolean
}

// Cognitive prediction result
export interface CognitivePrediction {
  assetId: string
  timestamp: string
  decision: string
  confidence: number
  confidenceInterval: ConfidenceInterval
  reasoningSteps: ReasoningStep[]
  keyFactors: string[]
  uncertaintySources: UncertaintySource[]
  similarCases: SimilarCase[]
  rootCauseAnalysis?: RootCauseAnalysis
  mode: CognitiveMode
  processingTimeMs: number
  storedInMemory: boolean
}

// RUL (Remaining Useful Life) prediction
export interface RULPrediction {
  assetId: string
  timestamp: string
  estimatedRUL: number // in hours
  confidenceInterval: ConfidenceInterval
  degradationRate: number // units per hour
  criticalThreshold: number
  timeToThreshold: number // hours
  factors: Array<{
    name: string
    impact: number
    trend: 'increasing' | 'decreasing' | 'stable'
  }>
}

// Evaluation metrics
export interface CognitiveEvaluation {
  humanLikenessScore: number
  reasoningQuality: number
  explanationClarity: number
  predictionAccuracy: number
  uncertaintyCalibration: number
  totalEvaluations: number
  lastUpdated: string
}

// WebSocket connection status
export type WsConnectionStatus = 'connected' | 'connecting' | 'disconnected' | 'error'

interface CognitiveState {
  // Cognitive predictions per asset
  predictions: Record<string, CognitivePrediction>

  // RUL predictions per asset
  rulPredictions: Record<string, RULPrediction>

  // Evaluation metrics
  evaluation: CognitiveEvaluation | null

  // Settings
  cognitiveEnabled: boolean
  autoExpandInsights: boolean
  showUncertainty: boolean
  selectedMode: CognitiveMode

  // WebSocket status
  wsStatus: WsConnectionStatus
  lastWsMessage: string | null

  // Loading states
  loading: Record<string, boolean>
  error: string | null

  // Actions - Predictions
  setCognitivePrediction: (assetId: string, prediction: CognitivePrediction) => void
  updateReasoningSteps: (assetId: string, steps: ReasoningStep[]) => void
  updateSimilarCases: (assetId: string, cases: SimilarCase[]) => void
  clearPrediction: (assetId: string) => void

  // Actions - RUL
  setRULPrediction: (assetId: string, rul: RULPrediction) => void
  updateRULDegradation: (assetId: string, rate: number) => void

  // Actions - Evaluation
  setEvaluation: (evaluation: CognitiveEvaluation) => void
  submitFeedback: (assetId: string, predictionId: string, correct: boolean, notes?: string) => void

  // Actions - Settings
  setCognitiveEnabled: (enabled: boolean) => void
  setAutoExpandInsights: (expand: boolean) => void
  setShowUncertainty: (show: boolean) => void
  setSelectedMode: (mode: CognitiveMode) => void

  // Actions - WebSocket
  setWsStatus: (status: WsConnectionStatus) => void
  setLastWsMessage: (message: string) => void

  // Actions - Loading
  setLoading: (assetId: string, loading: boolean) => void
  setError: (error: string | null) => void

  // Selectors
  getCognitivePrediction: (assetId: string) => CognitivePrediction | undefined
  getRULPrediction: (assetId: string) => RULPrediction | undefined
  getConfidenceInterval: (assetId: string) => ConfidenceInterval | undefined
  getUncertaintySources: (assetId: string) => UncertaintySource[]
  getSimilarCases: (assetId: string) => SimilarCase[]
  getReasoningChain: (assetId: string) => ReasoningStep[]
  isLoading: (assetId: string) => boolean
}

export const useCognitiveStore = create<CognitiveState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        predictions: {},
        rulPredictions: {},
        evaluation: null,
        cognitiveEnabled: true,
        autoExpandInsights: false,
        showUncertainty: true,
        selectedMode: 'deliberative',
        wsStatus: 'disconnected',
        lastWsMessage: null,
        loading: {},
        error: null,

        // Actions - Predictions
        setCognitivePrediction: (assetId, prediction) =>
          set((state) => ({
            predictions: {
              ...state.predictions,
              [assetId]: prediction
            },
            loading: {
              ...state.loading,
              [assetId]: false
            }
          })),

        updateReasoningSteps: (assetId, steps) =>
          set((state) => {
            const existing = state.predictions[assetId]
            if (!existing) return state
            return {
              predictions: {
                ...state.predictions,
                [assetId]: {
                  ...existing,
                  reasoningSteps: steps
                }
              }
            }
          }),

        updateSimilarCases: (assetId, cases) =>
          set((state) => {
            const existing = state.predictions[assetId]
            if (!existing) return state
            return {
              predictions: {
                ...state.predictions,
                [assetId]: {
                  ...existing,
                  similarCases: cases
                }
              }
            }
          }),

        clearPrediction: (assetId) =>
          set((state) => {
            const { [assetId]: _, ...rest } = state.predictions
            return { predictions: rest }
          }),

        // Actions - RUL
        setRULPrediction: (assetId, rul) =>
          set((state) => ({
            rulPredictions: {
              ...state.rulPredictions,
              [assetId]: rul
            }
          })),

        updateRULDegradation: (assetId, rate) =>
          set((state) => {
            const existing = state.rulPredictions[assetId]
            if (!existing) return state
            return {
              rulPredictions: {
                ...state.rulPredictions,
                [assetId]: {
                  ...existing,
                  degradationRate: rate
                }
              }
            }
          }),

        // Actions - Evaluation
        setEvaluation: (evaluation) => set({ evaluation }),

        submitFeedback: async (assetId, predictionId, correct, notes) => {
          try {
            // This would call the API
            console.log('Submitting feedback:', { assetId, predictionId, correct, notes })
            // Update local evaluation metrics optimistically
            const current = get().evaluation
            if (current) {
              set({
                evaluation: {
                  ...current,
                  totalEvaluations: current.totalEvaluations + 1,
                  lastUpdated: new Date().toISOString()
                }
              })
            }
          } catch (error) {
            console.error('Failed to submit feedback:', error)
          }
        },

        // Actions - Settings
        setCognitiveEnabled: (enabled) => set({ cognitiveEnabled: enabled }),
        setAutoExpandInsights: (expand) => set({ autoExpandInsights: expand }),
        setShowUncertainty: (show) => set({ showUncertainty: show }),
        setSelectedMode: (mode) => set({ selectedMode: mode }),

        // Actions - WebSocket
        setWsStatus: (status) => set({ wsStatus: status }),
        setLastWsMessage: (message) => set({ lastWsMessage: message }),

        // Actions - Loading
        setLoading: (assetId, loading) =>
          set((state) => ({
            loading: {
              ...state.loading,
              [assetId]: loading
            }
          })),

        setError: (error) => set({ error }),

        // Selectors
        getCognitivePrediction: (assetId) => get().predictions[assetId],

        getRULPrediction: (assetId) => get().rulPredictions[assetId],

        getConfidenceInterval: (assetId) => get().predictions[assetId]?.confidenceInterval,

        getUncertaintySources: (assetId) => get().predictions[assetId]?.uncertaintySources || [],

        getSimilarCases: (assetId) => get().predictions[assetId]?.similarCases || [],

        getReasoningChain: (assetId) => get().predictions[assetId]?.reasoningSteps || [],

        isLoading: (assetId) => get().loading[assetId] || false
      }),
      {
        name: 'cognitive-store',
        partialize: (state) => ({
          // Only persist settings, not data
          cognitiveEnabled: state.cognitiveEnabled,
          autoExpandInsights: state.autoExpandInsights,
          showUncertainty: state.showUncertainty,
          selectedMode: state.selectedMode
        })
      }
    ),
    { name: 'cognitive-store' }
  )
)

export default useCognitiveStore
